import { useLocalization } from '@components/Localization'
import { Box, Grid } from '@gassan-ui'
import { Text } from '@rolex-ui'
import React, { FC, useEffect, useReducer, useRef, useState } from 'react'
import filterOptions from './filterOptions'
import Filters from './Filters'
import RolexGrid from './Grid'
import { reducer } from './reducer'
import Sidebar from './Sidebar'
import { useUrlSync } from './useUrlSync'
import { getRolexProducts } from '@lib/kontent/getRolexProducts'
import { MultipleItemResponse } from '@lib/kontent'
import { RolexProduct } from '@lib/kontent/models/RolexProduct'
import Pagination from './Pagination'

interface Props {
  initialData: MultipleItemResponse<RolexProduct>
  style?: string[]
  size?: string[]
  material?: string[]
  dial?: string[]
  page?: number
  fid: string
}

const Overview: FC<Props> = ({ initialData, fid, ...initial }) => {
  const [data, setData] = useState(initialData)
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const isMounted = useRef(false)
  const prevFamily = useRef<null | string>(null)
  const { lang, messages } = useLocalization()

  const [state, dispatch] = useReducer(reducer, {
    style: initial.style || [],
    size: initial.size || [],
    material: initial.material || [],
    dial: initial.dial || [],
    page: initial.page || 0
  })

  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const { style, size, material, dial } = state

  // Some filters are fixed on some pages,
  // e.g. mens-watches has a fixed filter on { style: [men] }
  const fixed = filterOptions[fid].fixed
  // Get the fixed values
  const fixedValues = fixed
    ? Object.keys(fixed)
        .map(key => fixed[key])
        .flat()
    : []

  // Merge that with the currently set filters and filter if a key exists more then once
  // e.g ["gold", "gold"] -> []
  const merged = [...style, ...size, ...material, ...dial, ...fixedValues].filter(
    (el, index, self) => self.lastIndexOf(el) === self.indexOf(el)
  )
  // If there's one or more remaing, we can reset
  const disableReset = merged.length === 0

  useEffect(() => {
    // Reset state if family has been changed
    if (prevFamily.current && prevFamily.current !== fid) {
      dispatch({ type: 'reset', fixed })
    }

    async function getNewItems() {
      setHasError(false)
      setIsLoading(true)
      try {
        const items = await getRolexProducts({
          language: lang === 'sg' ? 'en' : lang,
          family: fid || undefined,
          ...state
        })
        setData(items)
        setIsLoading(false)
      } catch (error) {
        setHasError(true)
        setIsLoading(false)
      }
    }

    // We can assume that the data is filled with the data from getInitialProps.
    // To prevent a double render with the same data (useEffect only runs on the client),
    // we check if the component is already mounted (on the client side) and only fetch if it is.
    // This effectively passes the getNewItems call the very first time the component is mounted client side
    if (isMounted.current) {
      getNewItems()
    }
    isMounted.current = true
    prevFamily.current = fid
  }, [state, lang, fid]) // eslint-disable-line

  useUrlSync(state)
  return (
    <>
      <Grid
        gridTemplateColumns={{ _: '100%', large: '1fr 3fr' }}
        gridColumnGap="0"
        pb="3.75rem"
        maxWidth={1360}
        mx="auto"
        width={{ _: '100%', large: '95%' }}
      >
        <Sidebar
          isOpen={isSidebarOpen}
          onOpen={() => setIsSidebarOpen(true)}
          onClose={() => setIsSidebarOpen(false)}
          onConfirm={() => setIsSidebarOpen(false)}
          disableReset={disableReset}
          onReset={() => {
            dispatch({ type: 'reset', fixed })
          }}
        >
          <Filters fid={fid} dispatch={dispatch} disableReset={disableReset} {...state} />
        </Sidebar>
        <Box as="main">
          {hasError ? (
            <Text py={20} textAlign="center">
              {messages.rolex.overview.error}
            </Text>
          ) : data.pagination && data.pagination.count === 0 ? (
            <Text py={20} textAlign="center">
              {messages.rolex.overview.empty}
            </Text>
          ) : (
            <RolexGrid data={data} isLoading={isLoading} />
          )}
        </Box>
      </Grid>
      <Pagination
        limit={data.pagination.limit}
        currentPage={state.page === 0 ? 1 : state.page}
        totalCount={data.pagination.total_count}
        setPage={page => dispatch({ type: 'set-page', page })}
      />
    </>
  )
}

export default Overview
